import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITE_KEY } from '../../../api/conf';
import { PButton, PButtonBack } from '../../../style/partnerStylemain'
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { alert } from 'react-custom-alert';

type Props = {
    /** Ref code returned once an OTP has been requested/sent. Undefined = not yet requested. */
    refCode?: string;
    /** Mobile number, already masked server-side (e.g. "081-XXX-1234"). */
    maskedMobileNo?: string;
    /** Masked mobile number to display before the OTP has been requested (refCode is not set yet, so the caller must mask it client-side). */
    requestMaskedMobileNo?: string;
    /** Seconds remaining before the current OTP expires / a resend is allowed. Reset by the caller on each new refCode. */
    timeLeftSecond?: number;
    loading?: boolean;
    /** When true, shows a mobile-number input the user must confirm before the first OTP request (matches the identity-confirmation step used by the view-summary and fill-master-data flows). */
    askMobileConfirmation?: boolean;
    mobileNo?: string;
    onMobileNoChange?: (value: string) => void;
    /** Called with a single-use reCAPTCHA token the backend must verify before sending the OTP. */
    onRequestOtp: (captchaToken: string) => void;
    onVerifyOtp: (otp: string) => void;
    onCancel?: () => void;
    requestLabel?: string;
    verifyLabel?: string;
    requestDescription?: React.ReactNode;
    verifyDescription?: React.ReactNode;
}

const OTP_LENGTH = 6;
const MOBILE_PATTERN = /^[0-9]{10}$/;

const CAPTCHA_REQUIRED_MESSAGE = 'กรุณายืนยันว่าคุณไม่ใช่โปรแกรมอัตโนมัติ';

const isValidMobile = (value?: string | null) => value != null && MOBILE_PATTERN.test(value);
const isMobileInvalid = (value: string | undefined, submitted: boolean) =>
    value != null ? !isValidMobile(value) : submitted;

const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const useOtpVerification = (props: Props) => {
    const { refCode, timeLeftSecond, askMobileConfirmation, mobileNo, onRequestOtp, onVerifyOtp } = props;

    const [otp, setOtp] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [mobileSubmitted, setMobileSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState<number | undefined>(timeLeftSecond);
    const captchaRef = useRef<any>(null);

    useEffect(() => {
        setTimeLeft(timeLeftSecond);
        setOtp('');
        setSubmitted(false);
    }, [refCode, timeLeftSecond]);

    useEffect(() => {
        if (!timeLeft) return;
        const intervalId = setInterval(() => {
            setTimeLeft((prev) => (prev == null ? prev : prev - 1));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [timeLeft]);

    const handleRequestOtp = () => {
        if (askMobileConfirmation) {
            setMobileSubmitted(true);
            if (!isValidMobile(mobileNo)) {
                alert({ message: 'กรุณากรอกเบอร์มือถือให้ถูกต้อง', type: 'error' });
                return;
            }
        }
        const captchaToken = captchaRef.current?.getValue();
        if (!captchaToken) {
            alert({ message: CAPTCHA_REQUIRED_MESSAGE, type: 'error' });
            return;
        }
        // A token is single-use, so reset now; a failed request then needs a fresh tick.
        captchaRef.current.reset();
        onRequestOtp(captchaToken);
    }

    const handleVerify = () => {
        setSubmitted(true);
        if (otp.length !== OTP_LENGTH) {
            alert({ message: `กรุณากรอกรหัส OTP ให้ครบ ${OTP_LENGTH} หลัก`, type: 'error' });
            return;
        }
        onVerifyOtp(otp);
    }

    return { otp, setOtp, submitted, mobileSubmitted, timeLeft, captchaRef, handleRequestOtp, handleVerify };
}

const OtpCaptcha = ({ captchaRef }: { captchaRef: React.MutableRefObject<any> }) => (
    <div className='d-flex justify-content-center mt-3'>
        <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_SITE_KEY} hl="th" />
    </div>
)

type RequestOtpViewProps = {
    requestDescription?: React.ReactNode;
    requestMaskedMobileNo?: string;
    askMobileConfirmation?: boolean;
    mobileNo?: string;
    mobileSubmitted: boolean;
    onMobileNoChange?: (value: string) => void;
    captchaRef: React.MutableRefObject<any>;
    onRequestOtp: () => void;
    onCancel?: () => void;
    loading?: boolean;
    requestLabel: string;
}

const RequestOtpView = ({
    requestDescription,
    requestMaskedMobileNo,
    askMobileConfirmation,
    mobileNo,
    mobileSubmitted,
    onMobileNoChange,
    captchaRef,
    onRequestOtp,
    onCancel,
    loading,
    requestLabel,
}: RequestOtpViewProps) => (
    <>
        <div className='text-left mb-3' style={{ fontSize: 22, fontWeight: 300 }}>
            {requestDescription ?? (
                <span>กรุณาระบุเบอร์มือถือของท่านที่ใช้ในปัจจุบัน โดยรหัส OTP จะส่งไปยังเบอร์มือถือดังกล่าว</span>
            )}
            {requestMaskedMobileNo && (<><br /><span className='text-muted'>ส่งไปที่เบอร์ {requestMaskedMobileNo}</span></>)}
        </div>
        {askMobileConfirmation && (
            <form>
                <input
                    type="tel"
                    maxLength={10}
                    minLength={10}
                    className={'form-control ' + (isMobileInvalid(mobileNo, mobileSubmitted) ? 'is-invalid' : '')}
                    id="input-text-otp-mobile"
                    value={mobileNo ?? ''}
                    onChange={(e: any) => onMobileNoChange?.(e.target.value)}
                />
            </form>
        )}
        <OtpCaptcha captchaRef={captchaRef} />
        <div className='d-flex justify-content-center mt-3'>
            <div className='mr-2'><PButton onClick={onRequestOtp} id="button-request-otp" disabled={loading}>{requestLabel}</PButton></div>
            {onCancel && (<div><PButtonBack onClick={onCancel} id="button-back">ยกเลิก</PButtonBack></div>)}
        </div>
    </>
)

type VerifyOtpViewProps = {
    maskedMobileNo?: string;
    verifyDescription?: React.ReactNode;
    refCode: string;
    otp: string;
    submitted: boolean;
    timeLeft?: number;
    loading?: boolean;
    verifyLabel: string;
    onOtpChange: (value: string) => void;
    captchaRef: React.MutableRefObject<any>;
    onVerify: () => void;
    onRequestOtp: () => void;
}

const VerifyOtpView = ({
    maskedMobileNo,
    verifyDescription,
    refCode,
    otp,
    submitted,
    timeLeft,
    loading,
    verifyLabel,
    onOtpChange,
    captchaRef,
    onVerify,
    onRequestOtp,
}: VerifyOtpViewProps) => {
    const isOtpInvalid = submitted && otp.length !== OTP_LENGTH;
    const canResend = timeLeft == null || timeLeft <= 0;
    const countdownLabel = canResend ? '' : `(รับรหัสใหม่ใน ${formatCountdown(timeLeft as number)})`;

    return (
        <>
            <div className='text-left mb-1' style={{ fontSize: 22 }}>
                <span style={{ fontWeight: 300 }}>
                    {verifyDescription ?? 'กรุณากรอกรหัส OTP ที่ได้รับผ่าน SMS'}
                    <br />รหัสถูกส่งไปที่เบอร์
                </span>{' '}
                <b>{maskedMobileNo}</b>
            </div>
            <form>
                <label className='col-form-label text-muted' style={{ display: 'block', marginBottom: 0, paddingBottom: 0 }}>Ref. Code: {refCode}</label>
                <label className='col-form-label' style={{ display: 'block', marginTop: 0, paddingTop: 0 }}>รหัสยืนยัน</label>
                <input
                    type="text"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={OTP_LENGTH}
                    className={'form-control ' + (isOtpInvalid ? 'is-invalid' : '')}
                    style={{ width: '100%' }}
                    id="input-text-otp"
                    value={otp}
                    onChange={(e: any) => onOtpChange(e.target.value.replace(/[^0-9]/g, '').slice(0, OTP_LENGTH))}
                />
            </form>
            <div className='text-left mt-2 mb-2 text-muted'>
                <AccessTimeIcon className='mr-1' style={{ fontSize: 15 }} />
                <span style={{ fontSize: 16, fontWeight: 300 }}>
                    หากไม่ได้รับรหัสภายใน 10 นาที กรุณากดขอรับรหัสใหม่ {countdownLabel}
                </span>
            </div>
            <div className='d-flex flex-column mt-4 align-items-center'>
                <PButton onClick={onVerify} id="button-verify-otp" disabled={loading} style={{ width: '100%', padding: '6px' }}>{verifyLabel}</PButton>
                {canResend ? (
                    <>
                        <OtpCaptcha captchaRef={captchaRef} />
                        <div className='mt-2'><a className='link-not-underline' onClick={onRequestOtp}>รับรหัสใหม่</a></div>
                    </>
                ) : (
                    <div className='mt-2 link-disabled'>รับรหัสใหม่</div>
                )}
            </div>
        </>
    )
}

const PartnerOtpVerification = (props: Props) => {
    const {
        refCode,
        maskedMobileNo,
        requestMaskedMobileNo,
        askMobileConfirmation,
        mobileNo,
        onMobileNoChange,
        onCancel,
        loading,
        requestLabel = 'ขอรหัส OTP',
        verifyLabel = 'ยืนยัน',
        requestDescription,
        verifyDescription,
    } = props;

    const { otp, setOtp, submitted, mobileSubmitted, timeLeft, captchaRef, handleRequestOtp, handleVerify } = useOtpVerification(props);

    if (!refCode) {
        return (
            <RequestOtpView
                requestDescription={requestDescription}
                requestMaskedMobileNo={requestMaskedMobileNo}
                askMobileConfirmation={askMobileConfirmation}
                mobileNo={mobileNo}
                mobileSubmitted={mobileSubmitted}
                onMobileNoChange={onMobileNoChange}
                captchaRef={captchaRef}
                onRequestOtp={handleRequestOtp}
                onCancel={onCancel}
                loading={loading}
                requestLabel={requestLabel}
            />
        );
    }

    return (
        <VerifyOtpView
            maskedMobileNo={maskedMobileNo}
            verifyDescription={verifyDescription}
            refCode={refCode}
            otp={otp}
            submitted={submitted}
            timeLeft={timeLeft}
            loading={loading}
            verifyLabel={verifyLabel}
            onOtpChange={setOtp}
            captchaRef={captchaRef}
            onVerify={handleVerify}
            onRequestOtp={handleRequestOtp}
        />
    );
}

export default PartnerOtpVerification
